// Capitales de provincia/departamento + ciudades comerciales relevantes de cada país, para el
// selector de "Ruta de viaje" cuando la rendición no viene de una solicitud de viaje (no existe un
// catálogo geográfico completo en el sistema, ni siquiera en el módulo de Solicitud de Viaje). No
// pretende ser exhaustivo: por eso el selector siempre ofrece "Otro" para escribir la ciudad a mano.
export const CIUDADES_ECUADOR = [
  "Ambato", "Azogues", "Babahoyo", "Cuenca", "Durán", "Esmeraldas", "Francisco de Orellana", "Guaranda",
  "Guayaquil", "Ibarra", "Latacunga", "Loja", "Macas", "Machala", "Manta", "Milagro", "Nueva Loja",
  "Otavalo", "Portoviejo", "Puerto Baquerizo Moreno", "Puyo", "Quevedo", "Quito", "Riobamba", "Salinas",
  "Santa Elena", "Santo Domingo", "Tena", "Tulcán", "Zamora"
] as const

export const CIUDADES_PERU = [
  "Abancay", "Arequipa", "Ayacucho", "Cajamarca", "Callao", "Cerro de Pasco", "Chachapoyas", "Chiclayo",
  "Chimbote", "Cusco", "Huancavelica", "Huancayo", "Huánuco", "Huaraz", "Ica", "Iquitos", "Juliaca",
  "Lima", "Moquegua", "Moyobamba", "Piura", "Pucallpa", "Puerto Maldonado", "Puno", "Sullana", "Tacna",
  "Talara", "Tarapoto", "Trujillo", "Tumbes"
] as const

export const RENDICION_SECCIONES = ["viajes", "alimentacion", "movilizacion", "hospedaje", "varios", "cajaChica"] as const
export type RendicionSeccion = (typeof RENDICION_SECCIONES)[number]

export const RENDICION_SECCION_LABELS: Record<RendicionSeccion, string> = {
  alimentacion: "Alimentación",
  movilizacion: "Movilización",
  viajes: "Viajes Aéreos",
  hospedaje: "Hospedaje",
  varios: "Otras rendiciones",
  cajaChica: "Caja Chica"
}

export type RendicionEstado =
  | "BORRADOR"
  | "ENVIADA"
  | "EN_REVISION"
  | "CORRECCION_SOLICITADA"
  | "REENVIADA"
  | "APROBADA_ADMIN"
  | "PENDIENTE_CONTABILIDAD"
  | "PROCESADA"
  | "RECHAZADA"
  | "ANULADA"

export const RENDICION_ESTADO_LABELS: Record<RendicionEstado, string> = {
  BORRADOR: "Borrador",
  ENVIADA: "Enviada",
  EN_REVISION: "En revisión",
  CORRECCION_SOLICITADA: "Corrección solicitada",
  REENVIADA: "Reenviada",
  APROBADA_ADMIN: "Aprobada",
  PENDIENTE_CONTABILIDAD: "Pendiente de contabilidad",
  PROCESADA: "Procesada",
  RECHAZADA: "Rechazada",
  ANULADA: "Anulada"
}

export const RENDICION_ESTADO_COLOR: Record<RendicionEstado, string> = {
  BORRADOR: "gray",
  ENVIADA: "blue",
  EN_REVISION: "yellow",
  CORRECCION_SOLICITADA: "orange",
  REENVIADA: "cyan",
  APROBADA_ADMIN: "teal",
  PENDIENTE_CONTABILIDAD: "grape",
  PROCESADA: "green",
  RECHAZADA: "red",
  ANULADA: "dark"
}

export const ESTADOS_REVISABLES: RendicionEstado[] = ["ENVIADA", "EN_REVISION", "REENVIADA"]
export const ESTADOS_ANULABLES: RendicionEstado[] = ["BORRADOR", "ENVIADA"]
export const ESTADOS_APROBADOS: RendicionEstado[] = ["APROBADA_ADMIN", "PENDIENTE_CONTABILIDAD", "PROCESADA"]

export type ComprobanteDecisionEstado = "PENDIENTE" | "ACEPTADO" | "CORRECCION" | "NO_CONSIDERADO"

export const DECISION_LABELS: Record<ComprobanteDecisionEstado, string> = {
  PENDIENTE: "Pendiente de revisión",
  ACEPTADO: "Aceptado",
  CORRECCION: "En corrección",
  NO_CONSIDERADO: "No considerado"
}

export const DECISION_COLOR: Record<ComprobanteDecisionEstado, string> = {
  PENDIENTE: "gray",
  ACEPTADO: "green",
  CORRECCION: "orange",
  NO_CONSIDERADO: "red"
}

export const MOTIVOS_CORRECCION_LABELS: Record<string, string> = {
  ILEGIBLE: "Ilegible",
  VALOR_INCORRECTO: "Valor incorrecto",
  RUC_INCORRECTO: "RUC incorrecto",
  DOCUMENTO_INCOMPLETO: "Documento incompleto",
  CATEGORIA_INCORRECTA: "Categoría incorrecta",
  FALTA_INFORMACION: "Falta información",
  OTRO: "Otro"
}

export interface ComprobanteDecision {
  estado: ComprobanteDecisionEstado
  motivo?: string
  observacion?: string
  fechaLimite?: string
  por?: string
  at?: string
}

export interface RendicionConsumo {
  descripcion: string
  cantidad?: number | null
  precioUnitario?: number | null
  total?: number | null
}

export interface RendicionItem {
  consumos?: RendicionConsumo[]
  detalleConsumo?: string
  _id?: string
  fecha?: string
  razonSocial?: string
  ruc?: string
  tipoDocumento?: string
  numeroDocumento?: string
  detalle?: string
  asistentes?: string[]
  valor: number
  igv: number
  rc?: number
  servicio?: number
  total: number
  moneda?: string
  tipoCambio?: number
  valorBase?: number
  igvBase?: number
  rcBase?: number
  servicioBase?: number
  totalBase?: number
  comprobanteUrl?: string
  voucherUrl?: string
  extraidoPorIA: boolean
  editadoManualmente: boolean
  /** Fecha impresa en el comprobante cuando quedó fuera del período (no se tomó como fecha del gasto) */
  fechaDetectada?: string
  /** Por qué el comprobante quedó sin fecha (ilegible o fuera del período) */
  avisoFecha?: string
  /** Datos que la lectura automática no pudo leer y quedaron en blanco (el colaborador los completa) */
  camposPendientes?: string[]
  contabilidad?: {
    centroGastos?: string
    registro?: string
    observaciones?: string
  }
  origenPlanillaMovilidad?: boolean
  // Solo aplica a líneas de la sección "cajaChica" (formato AA-RE-14); queda vacío en el resto.
  medicoClinica?: string
  formaPago?: { origen?: "PROPIOS" | "TARJETA"; tarjetaId?: string }
  consumoTarjetaId?: string
  categoria?: string
  /** Caja Chica Perú: motivo de la excepción cuando el consumo supera el límite. */
  excepcionMotivo?: string
  decision?: ComprobanteDecision
  corregido?: boolean
  personasCentroCosto?: Array<{ nombre: string; centroCosto?: string }>
}

export interface RendicionExcludedItem extends RendicionItem {
  seccion: RendicionSeccion
  justificacion: string
  excludedBy?: string
  excludedAt?: string
}

// Yape/Plin son apps de pago exclusivas de Perú y solo tienen sentido en comprobantes de
// Movilización (taxis, parqueaderos, peajes pagados así); el resto de categorías, y Ecuador en
// general, no deben ofrecerlas como tipo de documento.
export const RENDICION_TIPOS_DOCUMENTO_NORMALES = ["Factura", "Boleta de venta", "Nota de venta"] as const
export const RENDICION_TIPOS_DOCUMENTO = ["Factura", "Boleta de venta", "Yape", "Plin"] as const

export interface RendicionCabecera {
  periodoDesde?: string
  periodoHasta?: string
  responsableNombre?: string
  responsableDocumento?: string
  fechaSalida?: string
  fechaLlegada?: string
  fechaLiquidacion?: string
  fechaDeposito?: string
  importeViaticos?: number
  rutaViaje?: string
  motivo?: string
  personasAsistentes?: string
  centroGastos?: string
  sucursal?: string
  // Solo aplica a rendiciones de Caja Chica (formato AA-RE-14).
  saldoInicial?: number
  anticipoMoneda?: "PEN" | "USD"
}

export interface RendicionTotales {
  alimentacion: number
  movilizacion: number
  viajes: number
  hospedaje: number
  varios: number
  cajaChica: number
  total: number
  saldo: number
  presentado?: number
  aceptado?: number
  enCorreccion?: number
  noConsiderado?: number
  pendiente?: number
  aprobado?: number
  propios?: number
  tarjeta?: number
  /** Perú (sin conversión): los mismos totales por cada moneda usada, p. ej. { USD: {...}, PEN: {...} } */
  porMoneda?: Record<string, Omit<RendicionTotales, "porMoneda">>
}

export interface RendicionPlanillaMovilidadItem {
  _id?: string
  fecha?: string
  motivo?: string
  destino?: string
  viaje?: string
  monto: number
  /** Moneda del día; sin valor es la de la rendición. */
  moneda?: "PEN" | "USD"
  comprobanteUrl?: string
}

export interface RendicionSustentoTecnicoEntry {
  _id?: string
  nombre?: string
  apellido?: string
  ocupacion?: string
}

export interface RendicionDeclaracionJuradaItem {
  _id?: string
  fecha?: string
  detalle?: string
  importe: number
}

export interface RendicionDeclaracionJurada {
  items: RendicionDeclaracionJuradaItem[]
  total: number
}

export interface RendicionObservacion {
  estado: RendicionEstado
  comentario?: string
  autorId?: string
  autorNombre?: string
  fecha: string
}

export interface RendicionHistorialEntry {
  accion: string
  estadoPrevio?: RendicionEstado
  estadoNuevo?: RendicionEstado
  performedBy: string
  performedAt: string
  comentario?: string
}

export type RendicionTipo = "VIATICOS" | "CAJA_CHICA" | "TARJETA_CORPORATIVA"

export const RENDICION_TIPO_LABELS: Record<RendicionTipo, string> = {
  VIATICOS: "VIÁTICOS",
  CAJA_CHICA: "CAJA CHICA",
  TARJETA_CORPORATIVA: "TARJETA CORPORATIVA"
}

export const RENDICION_TIPO_COLOR: Record<RendicionTipo, string> = {
  VIATICOS: "blue",
  CAJA_CHICA: "teal",
  TARJETA_CORPORATIVA: "grape"
}

export interface Rendicion {
  periodoMes?: string
  id: string
  codigo: string
  numero?: string
  tipo?: RendicionTipo
  tarjetaId?: string
  tarjetaEtiqueta?: string
  /** Caja Chica Perú: martes que cierra la semana de esta rendición. */
  periodoSemana?: string
  envioCaja?: { fueraDeFecha: boolean; diaRendicion: string; reposicionEstimada: string }
  redFlag?: { por: string; at: string; asunto: string; descripcion?: string; para: string; cc: string[] }
  formasPago?: { anticipo: boolean; propios: boolean; tarjeta: boolean }
  travelRequestId?: string
  travelRequestCodigo?: string
  apartados?: RendicionSeccion[]
  pais: string
  estado: RendicionEstado
  formatoVersion: string
  moneda: "PEN" | "USD"
  anticipoId?: string
  cabecera: RendicionCabecera
  alimentacion: RendicionItem[]
  movilizacion: RendicionItem[]
  viajes: RendicionItem[]
  hospedaje: RendicionItem[]
  varios: RendicionItem[]
  cajaChica: RendicionItem[]
  excludedItems?: RendicionExcludedItem[]
  planillaMovilidad: RendicionPlanillaMovilidadItem[]
  planillaAdjuntos?: string[]
  sustentoTecnico: RendicionSustentoTecnicoEntry[]
  declaracionJurada: RendicionDeclaracionJurada
  totales: RendicionTotales
  observaciones: RendicionObservacion[]
  historial: RendicionHistorialEntry[]
  revisadoPor?: string
  revisadoEn?: string
  enviadaAt?: string
  aprobadaAt?: string
  procesadaAt?: string
  version?: number
  ticketContableId?: string
  ticketContableNumero?: string
  /** Persona de Contabilidad a la que quedó asignado el ticket: es quien revisa y procesa la rendición. */
  contabilidadAsignada?: { id?: string; nombre?: string; correo?: string }
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface RendicionTravelRequestOption {
  periodos?: Array<{ mes: string; desde: string; hasta: string }>
  /** Último día (YYYY-MM-DD) para enviar la rendición: 5 días hábiles después del retorno. */
  plazoLimite?: string
  plazoVencido?: boolean
  _id: string
  codigo: string
  estado: string
  trip?: {
    ciudadOrigen?: string
    ciudadDestino?: string
    fechaViaje?: string
    fechaRetorno?: string
  }
}

export interface CreateRendicionInput {
  periodoMes?: string
  /** Caja Chica Perú: martes (YYYY-MM-DD) que cierra la semana que se rinde. */
  periodoSemana?: string
  pais: string
  apartados: RendicionSeccion[]
  travelRequestId?: string
  tipo?: RendicionTipo
  tarjetaId?: string
}

export interface FilterRendicionParams {
  page?: number
  limit?: number
  search?: string
  estado?: RendicionEstado
  pais?: string
  apartado?: RendicionSeccion
  tipo?: RendicionTipo
  responsable?: string
  numero?: string
  fechaEnvio?: string
  periodo?: string
}

export interface FilterRendicionReporteParams {
  search?: string
  estado?: RendicionEstado
  pais?: string
  fechaViajeDesde?: string
  fechaViajeHasta?: string
}

export interface RendicionReporteRow {
  id: string
  codigo: string
  numero?: string
  tipo?: RendicionTipo
  travelRequestCodigo?: string
  fechaViaje?: string
  fechaRetorno?: string
  fechaEntregaLiquidacion?: string
  cedula?: string
  nombre?: string
  cargo?: string
  area?: string
  pais: string
  // "VIAJE NACIONAL"/"VIAJE INTERNACIONAL" cuando hay viaje vinculado; sin viaje, la lista de
  // categorías de gasto de la rendición (p. ej. "Movilización, Alimentación"), u "OTROS" si no tiene.
  tipoLiquidacion: string
  valorAnticipo: number
  valorLiquidado: number
  diferencia: number
  /** Moneda de la rendición y total liquidado por cada moneda usada (Perú: soles y dólares sin convertir) */
  moneda?: string
  liquidadoPorMoneda?: Record<string, number>
  diasParaLiquidar?: number
  fechaRevision?: string
  cumplePolitica?: boolean
  estadoLiquidacion: RendicionEstado
  responsableRevision?: string
  motivoRechazo?: string
  observaciones?: string
}

export interface RendicionReporteSummary {
  total: number
  aprobadas: number
  rechazadas: number
  anuladas: number
  pendientes: number
  totalLiquidado: number
  totalLiquidadoPorMoneda?: Record<string, number>
}

// Roster de "quién tiene caja chica asignada, por cuánto y en qué país/área" (AA-RE-14). Solo estas
// personas pueden crear una rendición de Caja Chica; el monto asignado precarga y bloquea el "Monto
// de caja" de esa rendición. Perú no maneja cédula en su formato de referencia (solo nombre + área);
// Ecuador sí.
export interface CajaChicaAsignacion {
  id: string
  pais: string
  nombre: string
  cedula?: string
  cargo?: string
  area: string
  montoAsignado: number
  isActive: boolean
}

export interface CreateCajaChicaAsignacionInput {
  pais: string
  nombre: string
  cedula?: string
  cargo?: string
  area: string
  montoAsignado: number
}

export interface UpdateCajaChicaAsignacionInput {
  nombre?: string
  cedula?: string
  cargo?: string
  area?: string
  montoAsignado?: number
}

export function currencySymbol(moneda: string): string {
  return moneda === "USD" ? "$" : "S/"
}

/** En Perú una rendición puede llevar gastos en soles y en dólares sin convertirlos; en los demás países todo va en la moneda de la rendición. */
export const esPaisMultiMoneda = (pais?: string): boolean => pais === "Perú"

export interface BloqueMoneda {
  moneda: string
  simbolo: string
  t: Omit<RendicionTotales, "porMoneda">
}

/** Un bloque de totales por moneda usada (la de la rendición primero). Sin desglose por moneda, un solo bloque con los totales de siempre. */
export function bloquesMoneda(r: { moneda: string; totales?: RendicionTotales }): BloqueMoneda[] {
  const porMoneda = r.totales?.porMoneda
  if (porMoneda && Object.keys(porMoneda).length) {
    return Object.keys(porMoneda)
      .sort((a, b) => (a === r.moneda ? -1 : b === r.moneda ? 1 : a.localeCompare(b)))
      .map((moneda) => ({ moneda, simbolo: currencySymbol(moneda), t: porMoneda[moneda] }))
  }
  return [{ moneda: r.moneda, simbolo: currencySymbol(r.moneda), t: (r.totales ?? { alimentacion: 0, movilizacion: 0, viajes: 0, hospedaje: 0, varios: 0, cajaChica: 0, total: 0, saldo: 0 }) as RendicionTotales }]
}

/** "S/ 20.00 · $ 12.00": un monto de los totales por cada moneda; `alterno` es el campo a usar si `campo` no viene. */
export function montoPorMoneda(r: { moneda: string; totales?: RendicionTotales }, campo: keyof Omit<RendicionTotales, "porMoneda">, alterno?: keyof Omit<RendicionTotales, "porMoneda">): string {
  return bloquesMoneda(r)
    .map(({ simbolo, t }) => `${simbolo} ${Number(t[campo] ?? (alterno ? t[alterno] : undefined) ?? 0).toFixed(2)}`)
    .join(" · ")
}

export function getRendicionId(r: Rendicion): string {
  return r.id || (r as unknown as { _id: string })._id
}

export interface CicloCajaChica {
  corte: string
  desde: string
  hasta: string
  mes: string
  ocupado: boolean
  vencido: boolean
  numero?: string
}
